import React, { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import AdminPageShell from "./AdminPageShell";
import useFetch from "../../useFetch";
import { apiRequest } from "../../utils/api";
import Pagination from "../Pagination";

const statuses = [
  { value: "", label: "All Statuses" },
  { value: "submitted", label: "Submitted" },
  { value: "reviewed", label: "Reviewed" },
  { value: "shortlisted", label: "Shortlisted" },
  { value: "rejected", label: "Rejected" },
];

const formatDate = (date) => date ? new Date(date).toLocaleDateString() : "Not available";

const statusClass = (status) => {
  if (status === "rejected") return "danger";
  if (status === "shortlisted") return "warning";
  return "";
};

const AdminApplications = () => {
  const { jobId } = useParams();
  const [keyword, setKeyword] = useState("");
  const [status, setStatus] = useState("");
  const [appliedFilters, setAppliedFilters] = useState({ keyword: "", status: "" });
  const [page, setPage] = useState(1);
  const [refreshKey, setRefreshKey] = useState(0);
  const [message, setMessage] = useState(false);
  const [actionError, setActionError] = useState(false);

  const apiUrl = useMemo(() => {
    const params = new URLSearchParams();
    params.set("per_page", "10");
    params.set("page", page);
    if (jobId) params.set("job_id", jobId);
    if (appliedFilters.keyword) params.set("keyword", appliedFilters.keyword);
    if (appliedFilters.status) params.set("status", appliedFilters.status);
    if (refreshKey) params.set("_", refreshKey);

    return process.env.REACT_APP_BASE_API_URL + "/admin/applications?" + params.toString();
  }, [appliedFilters, jobId, page, refreshKey]);

  const { response, isLoading, error } = useFetch(apiUrl);
  const applications = response?.applications?.data ?? [];
  const meta = response?.applications?.meta ?? {};
  const total = meta?.total ?? applications.length;

  const applyFilters = (e) => {
    e.preventDefault();
    setPage(1);
    setAppliedFilters({ keyword: keyword.trim(), status });
  };

  const clearFilters = () => {
    setKeyword("");
    setStatus("");
    setPage(1);
    setAppliedFilters({ keyword: "", status: "" });
  };

  const updateStatus = (application, nextStatus) => {
    if (nextStatus === application.status) return;
    if (!window.confirm(`Change this application status to ${nextStatus}?`)) return;

    setMessage(false);
    setActionError(false);
    const formData = new FormData();
    formData.append("status", nextStatus);

    apiRequest(`/admin/applications/${application.id}/status`, { method: "POST", body: formData })
      .then((data) => {
        setMessage(data.message);
        setRefreshKey(Date.now());
      })
      .catch((error) => setActionError(error.message));
  };

  return (
    <AdminPageShell
      active="applications"
      title={jobId ? "Job Applicants" : "Applications"}
      text={jobId ? "Review and manage applicants submitted against this job." : "Search, filter, and manage candidate applications across the platform."}
    >
      <div className="ls-widget">
        <div className="widget-title">
          <h4>{jobId ? "Applicants" : "Application Management"}</h4>
          {jobId && <Link to="/admin-jobs" className="theme-btn btn-style-three">Back to Jobs</Link>}
        </div>

        <form className="admin-filter-bar" onSubmit={applyFilters}>
          <div className="admin-filter-field">
            <label>Search</label>
            <input
              type="text"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="Candidate, email, job, or employer"
            />
          </div>
          <div className="admin-filter-field">
            <label>Status</label>
            <select value={status} onChange={(e) => setStatus(e.target.value)}>
              {statuses.map((item) => (
                <option value={item.value} key={item.value}>{item.label}</option>
              ))}
            </select>
          </div>
          <div className="admin-filter-actions">
            <button type="submit" className="theme-btn btn-style-one">Apply</button>
            <button type="button" className="theme-btn btn-style-three" onClick={clearFilters}>Clear</button>
          </div>
        </form>

        <div className="widget-content">
          {message && <div className="alert alert-success"><strong>{message}</strong></div>}
          {actionError && <div className="alert alert-danger"><strong>{actionError}</strong></div>}
          {error && <div className="alert alert-danger"><strong>{error}</strong></div>}
          {isLoading && <h5 className="text-center my-3">Loading Data</h5>}

          {!error && !isLoading && (
            <div className="admin-list-summary">Showing {applications.length} of {total} applications</div>
          )}

          <div className="admin-table-list">
            {!error && !isLoading && applications.map((application) => (
              <div className="admin-table-row admin-application-row" key={application.id}>
                <div>
                  <strong>{application.candidate_name}</strong>
                  <span>{application.job_title} - {application.company_name} - {formatDate(application.created_at)}</span>
                  <span>{application.candidate_email}{application.candidate_phone ? ` - ${application.candidate_phone}` : ""}</span>
                </div>
                <div className="admin-row-actions">
                  <span className={statusClass(application.status)}>{application.status}</span>
                  <select
                    className="admin-status-select"
                    value={application.status}
                    onChange={(e) => updateStatus(application, e.target.value)}
                  >
                    {statuses.filter((item) => item.value).map((item) => (
                      <option value={item.value} key={item.value}>{item.label}</option>
                    ))}
                  </select>
                  {application.resume_url && (
                    <>
                      <a href={application.resume_url} target="_blank" rel="noreferrer" className="theme-btn btn-style-three">Open Resume</a>
                      {application.resume_download_url && (
                        <a href={application.resume_download_url} target="_blank" rel="noreferrer" className="theme-btn btn-style-three">Download</a>
                      )}
                    </>
                  )}
                  {application.job_id && <Link to={`/jobs/${application.job_id}`} className="theme-btn btn-style-three">Job</Link>}
                  {application.candidate_id && <Link to={`/admin-candidates/${application.candidate_id}`} className="theme-btn btn-style-three">Candidate</Link>}
                  {application.employer_id && <Link to={`/admin-employers/${application.employer_id}`} className="theme-btn btn-style-three">Employer</Link>}
                </div>
              </div>
            ))}
            {!error && !isLoading && applications.length === 0 && <div className="admin-empty-state">No applicants found.</div>}
          </div>
          {!error && !isLoading && <Pagination meta={meta} onPageChange={setPage} label="Application pagination" />}
        </div>
      </div>
    </AdminPageShell>
  );
};

export default AdminApplications;
